import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HashRouter, Navigate, Route, BrowserRouter, Routes } from "react-router-dom";
import { CommandPalette } from "@/components/CommandPalette";
import { GoogleCalendarAutoConnect } from "@/components/GoogleCalendarAutoConnect";
import { TauriBridge } from "@/components/TauriBridge";
import { AppShell } from "@/components/layout/AppShell";
import { RequireAuth } from "@/components/layout/RequireAuth";
import { isQuickCaptureWindow, isTauri } from "@/lib/platform";
import { ThemeProvider } from "@/lib/theme";
import { CalendarPage } from "@/routes/CalendarPage";
import { InvitePage } from "@/routes/InvitePage";
import { NotePage } from "@/routes/NotePage";
import { NotesPage } from "@/routes/NotesPage";
import { OAuthConsentPage } from "@/routes/OAuthConsentPage";
import { QuickCapturePage } from "@/routes/QuickCapturePage";
import { SettingsPage } from "@/routes/SettingsPage";
import { TasksPage } from "@/routes/TasksPage";
import { TeamsPage } from "@/routes/TeamsPage";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // The API is the source of truth; refetching on focus keeps a second
      // device's edits from lingering as stale reads.
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        // Never retry an auth or permission failure — it will not get better.
        const status = (error as { status?: number }).status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

/**
 * The mobile Tauri shells serve the built files from a custom protocol with no
 * SPA rewrite, so a reload on /notes/:id would 404. Hash routing keeps deep
 * links working there; the web build keeps clean URLs. Desktop now loads the
 * deployed app, which does rewrite, but stays on the hash router so the flying
 * note can be addressed as `#/quick` on every target.
 */
const Router = isTauri() ? HashRouter : BrowserRouter;

export default function App() {
  /*
   * The flying note is its own Tauri window pointed at #/quick. It is branched
   * before the router on purpose: it must never mount the shell, the command
   * palette or the auth gate's full-page sign-in screen, none of which fit — or
   * belong — in a 460x280 capture window.
   */
  if (isQuickCaptureWindow()) {
    return (
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <QuickCapturePage />
        </QueryClientProvider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <Router>
          <RequireAuth>
            <CommandPalette />
            <TauriBridge />
            <GoogleCalendarAutoConnect />
            <Routes>
              {/* Outside the shell: an invite is a standalone landing page. */}
              <Route path="/invite/:token" element={<InvitePage />} />
              {/* Also outside the shell: an OAuth consent screen with app
                  navigation around it invites clicking away mid-flow. */}
              <Route path="/oauth/consent" element={<OAuthConsentPage />} />
              <Route element={<AppShell />}>
                <Route index element={<Navigate to="/notes" replace />} />
                <Route path="/notes" element={<NotesPage />} />
                <Route path="/notes/:id" element={<NotePage />} />
                <Route path="/tasks" element={<TasksPage />} />
                <Route path="/calendar" element={<CalendarPage />} />
                <Route path="/teams" element={<TeamsPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="*" element={<Navigate to="/notes" replace />} />
              </Route>
            </Routes>
          </RequireAuth>
        </Router>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
